import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, X, Plus, RotateCcw, ScanLine, Upload } from "lucide-react";

// Senior-friendly document scanner: live camera, multi-page capture, big buttons.
// onSubmit(files: File[]) is called with JPEG File objects. Parent controls `submitting`.
export function CameraScanner({ open, onClose, onSubmit, submitting, readingLabel }) {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [camState, setCamState] = useState("starting"); // starting | ready | error

  const stopCamera = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((tr) => tr.stop()); } catch {}
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setCamState("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCamState("ready");
    } catch {
      setCamState("error");
    }
  }, []);

  useEffect(() => {
    if (open) startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const capture = () => {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas || camState !== "ready") return;
    const w = video.videoWidth, h = video.videoHeight;
    if (!w || !h) return;
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `page-${pages.length + 1}-${Date.now()}.jpg`, { type: "image/jpeg" });
      setPages((p) => [...p, { url: URL.createObjectURL(blob), file }]);
    }, "image/jpeg", 0.9);
  };

  const removePage = (i) => setPages((p) => {
    try { URL.revokeObjectURL(p[i]?.url); } catch {}
    return p.filter((_, idx) => idx !== i);
  });

  const reset = () => {
    pages.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
    setPages([]);
  };

  const onNativeFiles = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setPages((p) => [...p, ...files.map((f) => ({ url: URL.createObjectURL(f), file: f }))]);
    e.target.value = "";
  };

  const submit = () => {
    if (!pages.length) { toast.message(t("scan_no_pages")); return; }
    onSubmit(pages.map((p) => p.file));
  };

  const close = () => { if (submitting) return; stopCamera(); reset(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="camera-scanner" className="max-w-lg p-0 gap-0 max-h-[92vh] overflow-y-auto flex flex-col">
        <DialogHeader className="px-5 pt-5 pb-2 shrink-0">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl brand-gradient text-white mb-1"><ScanLine className="h-6 w-6" /></div>
          <DialogTitle className="font-display text-2xl text-navy">{t("scan_title")}</DialogTitle>
          <DialogDescription className="text-slate-500 text-base">{t("scan_intro")}</DialogDescription>
        </DialogHeader>

        <div className="relative mx-5 mt-1 aspect-[4/3] max-h-[34vh] w-auto shrink-0 overflow-hidden rounded-2xl bg-slate-900">
          <video ref={videoRef} playsInline muted className={`h-full w-full object-cover ${camState === "ready" ? "" : "opacity-0"}`} data-testid="scanner-video" />
          <canvas ref={canvasRef} className="hidden" />

          {camState === "starting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/90">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">{t("scan_starting_camera")}</p>
            </div>
          )}

          {camState === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-white/90">
              <Camera className="h-7 w-7" />
              <p className="text-sm">{t("scan_cam_error")}</p>
            </div>
          )}

          {submitting && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-navy/80 text-white" data-testid="scanner-reading">
              <Loader2 className="h-9 w-9 animate-spin" />
              <p className="font-medium">{readingLabel || t("scan_reading")}</p>
              <p className="text-xs text-white/70 max-w-[16rem] text-center">{t("scan_reading_hint")}</p>
            </div>
          )}

          {camState === "ready" && !submitting && (
            <div className="pointer-events-none absolute inset-4 rounded-xl border-2 border-white/50" aria-hidden="true" />
          )}
        </div>

        {/* captured pages */}
        {pages.length > 0 && (
          <div className="px-5 pt-3 shrink-0">
            <p className="text-sm font-medium text-navy">{t("scan_pages_added", { n: pages.length })}</p>
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="scanner-pages">
              {pages.map((p, i) => (
                <div key={i} className="relative shrink-0">
                  <img src={p.url} alt={`${t("scan_page")} ${i + 1}`} className="h-16 w-12 rounded-lg object-cover border border-slate-200" />
                  <button onClick={() => removePage(i)} disabled={submitting} data-testid={`scanner-remove-${i}`}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2.5 p-5 shrink-0">
          {camState === "ready" && (
            <button data-testid="scanner-capture" onClick={capture} disabled={submitting}
              className="flex items-center justify-center gap-2 rounded-full brand-gradient py-4 text-lg font-semibold text-white card-shadow-lg transition-transform hover:scale-[1.02] disabled:opacity-60">
              <Camera className="h-6 w-6" />
              {pages.length ? t("scan_add_page") : t("scan_capture")}
            </button>
          )}

          {/* Always-visible, senior-friendly upload button */}
          <Button data-testid="scanner-upload" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={submitting}
            className="rounded-full py-6 text-base font-semibold border-2 border-brandblue/40 text-navy hover:bg-brandblue/5">
            <Upload className="h-5 w-5 mr-2 text-brandblue" /> {t("scan_upload_file")}
          </Button>

          {pages.length > 0 && (
            <Button data-testid="scanner-submit" onClick={submit} disabled={submitting}
              className="rounded-full bg-brandteal py-6 text-base font-semibold text-white hover:bg-brandteal/90">
              {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <ScanLine className="h-5 w-5 mr-2" />}
              {t("scan_done", { n: pages.length })}
            </Button>
          )}

          {pages.length > 0 && (
            <button data-testid="scanner-reset" onClick={reset} disabled={submitting}
              className="mx-auto inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy">
              <RotateCcw className="h-4 w-4" /> {t("scan_retake")}
            </button>
          )}
        </div>

        <input ref={fileInputRef} type="file" hidden accept="image/*" capture="environment" multiple
          data-testid="scanner-file-input" onChange={onNativeFiles} />
      </DialogContent>
    </Dialog>
  );
}
